// Small presentational pieces shared by the rating page states (rate, thanks,
// invalid/expired): the site thumbnail, the "no incentives" shield badge, the
// notice box, pill buttons/links and the underlined accent link. Everything is
// styled from ratingPalette — no MUI theme access so the page renders
// identically in Jest and in the portal.

import VerifiedUserOutlined from "@mui/icons-material/VerifiedUserOutlined";
import { makeStyles } from "@mui/styles";

import {
  RATING_COLORS,
  RATING_MOBILE_MEDIA_QUERY,
} from "~/shared/styles/ratingPalette";
import thumb from "~assets/images/rating/gokick-home-thumb.png";

const useStyles = makeStyles({
  // Framed variant: white square tile with the site screenshot letterboxed
  // inside (the rate page's company card).
  thumbFrame: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    backgroundColor: "#FFFFFF",
    border: `1px solid ${RATING_COLORS.thumbBorder}`,
    borderRadius: "8px",
    overflow: "hidden",
  },
  thumbFramedImg: {
    display: "block",
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  // Bare variant: the screenshot at its natural aspect, no tile (thank-you).
  thumbBare: {
    display: "block",
    flexShrink: 0,
    width: "auto",
  },
  notice: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    backgroundColor: RATING_COLORS.noticeBox,
    border: `1px solid ${RATING_COLORS.borderCard}`,
    borderRadius: "8px",
    padding: "16px",
    fontSize: "16px",
    lineHeight: 1.5,
    color: RATING_COLORS.textPrimary,
  },
  // Trustpilot's incentives notice: tighter, 14px, badge centred on the text.
  noticeCompact: {
    alignItems: "center",
    gap: "14px",
    padding: "12px",
    fontSize: "14px",
    lineHeight: "20px",
  },
  noticeIcon: {
    color: RATING_COLORS.accent,
    flexShrink: 0,
    marginTop: "1px",
  },
  noticeBadge: {
    flexShrink: 0,
  },
  noticeBody: {
    minWidth: 0,
  },
  noticeTitle: {
    fontWeight: 600,
    margin: "0 0 4px",
  },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    height: "48px",
    padding: "0 32px",
    borderRadius: "24px",
    border: 0,
    backgroundColor: RATING_COLORS.accent,
    color: "#FFFFFF",
    fontFamily: "inherit",
    fontSize: "16px",
    fontWeight: 600,
    lineHeight: 1,
    textDecoration: "none",
    whiteSpace: "nowrap",
    cursor: "pointer",
    transition: "background-color 120ms ease",
    WebkitTapHighlightColor: "transparent",
    "&:hover": {
      backgroundColor: RATING_COLORS.accentHover,
      color: "#FFFFFF",
    },
    "&:focus-visible": {
      outline: `2px solid ${RATING_COLORS.accent}`,
      outlineOffset: "2px",
    },
    "&:disabled": {
      opacity: 0.5,
      cursor: "not-allowed",
      backgroundColor: RATING_COLORS.accent,
    },
    [RATING_MOBILE_MEDIA_QUERY]: {
      width: "100%",
    },
  },
  // Header pill ("For businesses"): soft blue, dark text, 44px.
  pillSoft: {
    height: "44px",
    padding: "0 16px",
    borderRadius: "22px",
    backgroundColor: RATING_COLORS.accentSoftBg,
    color: RATING_COLORS.accentSoftText,
    "&:hover": {
      backgroundColor: RATING_COLORS.accentSoftBg,
      color: RATING_COLORS.accentSoftText,
      filter: "brightness(0.95)",
    },
    [RATING_MOBILE_MEDIA_QUERY]: {
      width: "auto",
    },
  },
  // Pale pill ("Get started"): pale blue, accent text, 44px, 14px.
  pillPale: {
    height: "44px",
    padding: "0 24px",
    borderRadius: "22px",
    backgroundColor: RATING_COLORS.accentPaleBg,
    color: RATING_COLORS.accent,
    fontSize: "14px",
    "&:hover": {
      backgroundColor: RATING_COLORS.accentPaleBg,
      color: RATING_COLORS.accentHover,
      filter: "brightness(0.97)",
    },
  },
  link: {
    color: RATING_COLORS.accent,
    textDecoration: "underline",
    textUnderlineOffset: "2px",
    cursor: "pointer",
    "&:hover": {
      color: RATING_COLORS.accentHover,
    },
  },
  linkButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: 0,
    border: 0,
    background: "none",
    fontFamily: "inherit",
    fontSize: "inherit",
    lineHeight: "inherit",
    borderRadius: "2px",
    "&:focus-visible": {
      outline: `2px solid ${RATING_COLORS.accent}`,
      outlineOffset: "2px",
    },
  },
});

// The gokick.com thumbnail. `framed` = white square tile with a 1px border
// (rate page company card); otherwise the bare screenshot at `height`.
export function SiteThumb({ size = 56, framed = true, height = 30 }) {
  const classes = useStyles();
  if (framed) {
    return (
      <span
        className={classes.thumbFrame}
        style={{ width: size, height: size }}
      >
        <img src={thumb} alt="" className={classes.thumbFramedImg} />
      </span>
    );
  }
  return (
    <img src={thumb} alt="" className={classes.thumbBare} style={{ height }} />
  );
}

// Blue shield with a dark star — Trustpilot's "no incentives" badge (24×30).
export function ShieldBadge({ className }) {
  return (
    <svg
      className={className}
      width="24"
      height="30"
      viewBox="0 0 24 30"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M12 1.5l10 3.6v9.2c0 6.6-4.3 11.9-10 14.2C6.3 26.2 2 20.9 2 14.3V5.1z"
        fill={RATING_COLORS.badgeFill}
        stroke={RATING_COLORS.badgeRim}
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M12 7.2l1.9 3.9 4.3.6-3.1 3 .8 4.3L12 17l-3.9 2 .8-4.3-3.1-3 4.3-.6z"
        fill={RATING_COLORS.badgeGlyph}
      />
    </svg>
  );
}

// Cream notice box with a leading icon. `compact` is the incentives notice
// (14px, padding 12, badge centred); the default is used by the titled
// invalid/expired/error notices.
export function NoticeBox({
  title,
  icon,
  compact = false,
  children,
  className,
}) {
  const classes = useStyles();
  const Icon = icon || VerifiedUserOutlined;
  return (
    <div
      className={[classes.notice, compact && classes.noticeCompact, className]
        .filter(Boolean)
        .join(" ")}
    >
      {compact ? (
        <ShieldBadge className={classes.noticeBadge} />
      ) : (
        <Icon className={classes.noticeIcon} fontSize="small" />
      )}
      <div className={classes.noticeBody}>
        {title ? <p className={classes.noticeTitle}>{title}</p> : null}
        <div>{children}</div>
      </div>
    </div>
  );
}

export function PillButton({
  type = "button",
  disabled = false,
  busy = false,
  onClick,
  children,
  className,
}) {
  const classes = useStyles();
  return (
    <button
      type={type}
      disabled={disabled}
      aria-busy={busy ? "true" : undefined}
      onClick={onClick}
      className={[classes.pill, className].filter(Boolean).join(" ")}
    >
      {children}
    </button>
  );
}

// variant: "primary" (accent, 48px) | "soft" (header pill) | "pale".
export function PillLink({ href, variant = "primary", children, className }) {
  const classes = useStyles();
  return (
    <a
      href={href}
      className={[
        classes.pill,
        variant === "soft" && classes.pillSoft,
        variant === "pale" && classes.pillPale,
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </a>
  );
}

export function RatingLink({ href, children, className, ...rest }) {
  const classes = useStyles();
  return (
    <a
      href={href}
      className={[classes.link, className].filter(Boolean).join(" ")}
      {...rest}
    >
      {children}
    </a>
  );
}

// A <button> that looks like RatingLink (for in-page toggles like "Want a tip?").
export function RatingLinkButton({ children, className, ...rest }) {
  const classes = useStyles();
  return (
    <button
      type="button"
      className={[classes.link, classes.linkButton, className]
        .filter(Boolean)
        .join(" ")}
      {...rest}
    >
      {children}
    </button>
  );
}
